import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CONTENT_GROUP_TYPES } from './constants';
import ContentGroupTabs from './ContentGroupTabs';
import ContentGroupAccordion from './ContentGroupAccordion';
import ContentGroupDataFetcher from './ContentGroupDataFetcher';
import { getSectionsData, buildDataSections } from './helpers';
import getStructuredDataProperties from './helpers/get-structured-data-properties';
import { getCustomHtmlProperties } from '../../helpers';

const VALID_TYPES = Object.values(CONTENT_GROUP_TYPES);

const ContentGroup = ({
  contentType,
  isFaqContent,
  children,
  customHtmlProps,
  simpleData,
  dataProperty,
  ...props
}) => {
  const hasSimpleData = Array.isArray(simpleData) && simpleData.length > 0;
  const dataPropertyName = Array.isArray(dataProperty) ? dataProperty[0] : dataProperty;
  const hasDataProperty = !!dataPropertyName;

  const childGroupSections = children?.props?.children?.[1];

  const GroupComponent = useMemo(
    () =>
      contentType === CONTENT_GROUP_TYPES.ACCORDION ? ContentGroupAccordion : ContentGroupTabs,
    [contentType]
  );
  const WrapperComponent = useMemo(() => (!isFaqContent ? Fragment : 'div'), [isFaqContent]);

  const { topWrapperProps, ...schemaProperties } = useMemo(() => {
    const structuredDataProperties = getStructuredDataProperties(isFaqContent);
    return {
      ...structuredDataProperties,
      topWrapperProps: {
        ...structuredDataProperties.topWrapperProps,
        ...getCustomHtmlProperties({ customHtmlProps })
      }
    };
  }, [isFaqContent, customHtmlProps]);

  if (!VALID_TYPES.includes(contentType)) return '';

  if (!hasSimpleData && hasDataProperty) {
    return (
      <WrapperComponent {...topWrapperProps}>
        <ContentGroupDataFetcher
          dataProperty={dataPropertyName}
          contentType={contentType}
          {...props}
          {...schemaProperties}
        />
      </WrapperComponent>
    );
  }

  let groupSections;
  let sectionsData;

  if (hasSimpleData) {
    ({ groupSections, sectionsData } = buildDataSections(simpleData));
  } else {
    groupSections = childGroupSections;
    if (!groupSections || !groupSections.length) return '';
    sectionsData = getSectionsData(groupSections);
  }
  if (!groupSections || !groupSections.length) return '';

  return (
    <WrapperComponent {...topWrapperProps}>
      <GroupComponent
        contentType={contentType}
        groupSections={groupSections}
        sectionsData={sectionsData}
        {...props}
        {...schemaProperties}
      />
    </WrapperComponent>
  );
};

ContentGroup.propTypes = {
  children: PropTypes.object,
  contentType: PropTypes.oneOf(VALID_TYPES).isRequired,
  isFaqContent: PropTypes.bool,
  customHtmlProps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string
    })
  ),
  simpleData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      text: PropTypes.string
    })
  ),
  dataProperty: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
};

ContentGroup.defaultProps = {
  children: null,
  isFaqContent: false,
  customHtmlProps: [],
  simpleData: null,
  dataProperty: null
};

export default ContentGroup;
